import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, TextField, Button, Typography,
  IconButton, InputAdornment, Alert, Link,
} from '@mui/material';
import { Visibility, VisibilityOff, AdminPanelSettings } from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { getDefaultRoute } from '../../utils/permissions';

const AdminLoginPage = () => {
  const navigate = useNavigate();
  const { login, loginAsRole } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleLogin = async () => {
    setError('');
    if (!email.trim() || !password) {
      setError('Please enter your email and password');
      return;
    }
    setLoading(true);
    try {
      const success = await login(email, password, false);
      if (success) {
        if (success.role !== 'admin') {
          setError('Access denied. This portal is for admin users only.');
          return;
        }
        navigate(getDefaultRoute(success.role));
      } else {
        setError('Invalid email or password');
      }
    } catch (err) {
      setError('Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  const handleDemoLogin = async () => {
    setError('');
    setLoading(true);
    try {
      const user = await loginAsRole('admin');
      if (user) navigate(getDefaultRoute('admin'));
    } catch (err) {
      setError('Demo login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') handleLogin();
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #37474F 0%, #263238 50%, #1B365D 100%)',
        p: 2,
      }}
    >
      <Box sx={{ width: '100%', maxWidth: 420 }}>
        {/* Admin badge */}
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <AdminPanelSettings sx={{ fontSize: 48, color: '#EF5350', mb: 1 }} />
          <Typography variant="h4" sx={{ color: '#fff', fontWeight: 800, letterSpacing: 1 }}>
            iMoney
          </Typography>
          <Typography variant="subtitle1" sx={{ color: 'rgba(255,255,255,0.7)', fontWeight: 600 }}>
            Admin Portal
          </Typography>
        </Box>

        <Card sx={{ borderRadius: 3, boxShadow: '0 8px 32px rgba(0,0,0,0.25)' }}>
          <CardContent sx={{ p: 4 }}>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <TextField
              fullWidth label="Admin Email" type="email"
              value={email} onChange={(e) => setEmail(e.target.value)}
              onKeyPress={handleKeyPress} sx={{ mb: 2 }}
            />

            <TextField
              fullWidth label="Password"
              type={showPassword ? 'text' : 'password'}
              value={password} onChange={(e) => setPassword(e.target.value)}
              onKeyPress={handleKeyPress}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 3 }}
            />

            <Button
              fullWidth variant="contained" onClick={handleLogin} disabled={loading}
              sx={{
                py: 1.5, backgroundColor: '#C62828', fontWeight: 600, fontSize: '1rem',
                textTransform: 'none', borderRadius: 2,
                '&:hover': { backgroundColor: '#B71C1C' },
              }}
            >
              {loading ? 'Signing in...' : 'Sign In as Admin'}
            </Button>

            {/* Demo quick-login */}
            <Button
              fullWidth variant="outlined" onClick={handleDemoLogin} disabled={loading}
              startIcon={<AdminPanelSettings />}
              sx={{
                mt: 2, py: 1, textTransform: 'none', fontWeight: 600, borderRadius: 2,
                borderColor: '#C62828', color: '#C62828',
                '&:hover': { borderColor: '#C62828', backgroundColor: 'rgba(198,40,40,0.04)' },
              }}
            >
              Demo Admin Login
            </Button>
          </CardContent>
        </Card>

        <Typography variant="body2" sx={{ textAlign: 'center', mt: 3, color: 'rgba(255,255,255,0.6)' }}>
          Not an admin?{' '}
          <Link
            component="button" onClick={() => navigate('/login')}
            sx={{ color: '#00B894', fontWeight: 600, textDecoration: 'none' }}
          >
            Go to User Login
          </Link>
        </Typography>
      </Box>
    </Box>
  );
};

export default AdminLoginPage;
